/**
 * Admin Route Guard
 * Protects admin routes - requires admin or super-admin role
 * Also blocks access during impersonation to prevent confusion
 *
 * NOTE: App.tsx guarantees authReadyState === 'ready' before router renders,
 * so this component can trust that roles are already loaded.
 */

import React, { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuth } from '../../hooks/useAuth';
import { useAppDispatch } from '../../hooks/redux';
import { fetchRolesFromAccessToken } from '../../store/slices/authSlice';
import { RootState } from '../../store';
import { logger } from '../../utils/logger';

interface AdminRouteProps {
  children: React.ReactNode;
}

export function AdminRoute({ children }: AdminRouteProps) {
  const dispatch = useAppDispatch();
  const { user, authReadyState, rolesLoading, rolesInitialized } = useAuth();
  const { isImpersonating } = useSelector((state: RootState) => state.impersonation);
  const roleRefreshAttemptedRef = useRef(false);
  const deniedLoggedRef = useRef(false);

  // Check if user has admin or super-admin role
  const hasAdminAccess = user?.roles?.includes('admin') || user?.roles?.includes('super-admin');

  const shouldAttemptRoleRefresh =
    authReadyState === 'ready' &&
    user &&
    !hasAdminAccess &&
    !rolesLoading &&
    !rolesInitialized &&
    !roleRefreshAttemptedRef.current;

  // One-time retry: roles can load after auth ready on direct loads
  useEffect(() => {
    if (shouldAttemptRoleRefresh) {
      roleRefreshAttemptedRef.current = true;
      logger.debug('AdminRoute attempting role refresh', {
        event: 'auth.admin.role_refresh',
      });
      dispatch(fetchRolesFromAccessToken());
    }
  }, [shouldAttemptRoleRefresh, dispatch]);

  const shouldShowSpinner =
    (authReadyState !== 'ready' && authReadyState !== 'unauthenticated') ||
    (authReadyState === 'ready' && (rolesLoading || !rolesInitialized)) ||
    shouldAttemptRoleRefresh;

  if (shouldShowSpinner) {
    logger.trace('AdminRoute showing spinner', {
      event: 'auth.admin.waiting',
      authReadyState,
      rolesLoading,
      rolesInitialized,
      shouldAttemptRoleRefresh,
    });
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  // Block admin access during impersonation
  if (isImpersonating) {
    if (!deniedLoggedRef.current) {
      deniedLoggedRef.current = true;
      logger.warn('Admin access denied during impersonation', {
        event: 'auth.admin.denied',
        reason: 'impersonating',
      });
    }
    return <Navigate to="/dashboard" replace />;
  }

  if (!hasAdminAccess) {
    if (!deniedLoggedRef.current) {
      deniedLoggedRef.current = true;
      logger.warn('Admin access denied', {
        event: 'auth.admin.denied',
        roles: user?.roles,
      });
    }
    return <Navigate to="/unauthorized" replace />;
  }

  logger.trace('AdminRoute access granted', { event: 'auth.admin.granted' });
  return <>{children}</>;
}
